import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../app/store";
// ── OLD Oracle imports (commented — uncomment to revert to Oracle) ──
// import { fetchEmpOracleHierarchyDetails } from "../Reportees/GetHierarchyDetailsSlice";
// import { fetchEmpOracleDetails, resetEmpOracleDetails } from "../CareerCard/EmpOracleDetailsSlice";
// ── NEW PostgreSQL OrgChart imports ──
import { fetchOrgChartEmployee, fetchOrgChartDirects, resetOrgChart } from "../Reportees/OrgChartSlice";
import type { OrgChartEmployee, OrgChartDirect } from "../Reportees/OrgChartSlice";
import { fetchEmpDetails, resetEmpDetails } from "../CareerCard/CareerCardDetailsSlice";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { createRoot } from "react-dom/client";
import CareerCardHtml from "../CareerCard/CareerCardPdfTemplate";
import type { EmpDetails } from "../CareerCard/CareerCardDetailsSlice";
import "./EmployeeHierarchysearch.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const Toast: React.FC<{ message: string; type: 'success' | 'error' | 'warning'; onClose: () => void }> = ({ message, type, onClose }) => (
    <div className={`hierarchy-toast hierarchy-toast--${type}`}>
        <span style={{ flex: 1 }}>{message}</span>
        <button className="hierarchy-toast__close" onClick={onClose} aria-label="Close notification">
            <i className="bi bi-x" aria-hidden="true" />
        </button>
    </div>
);

interface Employee {
    name: string;
    oracleId: string;
    email: string;
    jobLevel: string;
    jobTitle: string;
    timeAtTTEC: string;
    imageUrl: string;
    managerId: string;
    managerName: string;
    reportees: Employee[];
    isExpanded: boolean;
    level: number;
}

interface ToastNotification {
    id: number;
    message: string;
    type: 'success' | 'error' | 'warning';
}

interface ExportProgress {
    current: number;
    total: number;
    phase: string;
}

interface LoadingProgress {
    show: boolean;
    message: string;
    current: number;
    total: number;
}

interface ProcessedEmployeeData {
    name: string;
    jobLevel: string;
    jobTitle: string;
    timeAtTTEC: string;
    time_in_current_role: string;
    education_degree: string;
    certificates: string;
    location_desc: string;
    willingness_to_relocate: string;
    next_role_preference: string;
    timing_for_next_role: string;
    last_year_top_achievement: string;
    last_year_personal_development_review: string;
    goal_next_two_quarters: string;
    future_career_aspirations: string;
    redo_for_previous_business_year: string;
    image: string;
    emp_languages: any[];
    EmpLocationPreferences: any[];
    work_experiences: any[];
    EmpResourceGroup: any[];
    EmpTtecCommunities: any[];
    EmpLeadershipCulture: any[];
    oracle_id: string;
    manager_oracle: string;
    manager_email: string;
    country: string;
    employee_status: string;
    accomplishments: string;
    redo: string;
    goals: string;
    resourceGroup: string;
    ttecCommunity: string;
    strength: string;
    development: string;
    ttecRoles: any[];
    languages: any[];
    locationPreferences: any[];
    strengthsDisplayed: any[];
    areasOfDevelopment: any[];
    externalRoles: any[];
}

interface OracleEmployeeMessage {
    Employee_Oracle?: string;
    Employee_FirstName?: string;
    Employee_LastName?: string;
    Manager_Oracle?: string;
    Manager_Email?: string;
    Country?: string;
    Location_Desc?: string;
    Employee_Status?: string;
    Employee_Level?: string;
    Job_Code?: string;
    Employee_Image?: string;
    Hire_Date?: string;
}

interface OracleDetailsResponse {
    data?: {
        response?: {
            message?: OracleEmployeeMessage;
        };
    };
}

/* OLD Oracle interfaces (commented uncomment to revert to Oracle)

interface HierarchyEmployeeData {
    employee_id: string;
    employee_name: string;
    work_email: string;
    job_level?: string;
    hire_date?: string;
    manager_name?: string;
    manager_id?: string;
}

interface HierarchyResponse {
    data?: {
        response?: {
            message?: {
                data?: HierarchyEmployeeData[];
                result?: {
                    returnvals?: {
                        [key: string]: {
                            value?: HierarchyEmployeeData[];
                        };
                    };
                };
            };
        };
    };
    response?: {
        message?: {
            data?: HierarchyEmployeeData[];
            result?: {
                returnvals?: {
                    [key: string]: {
                        value?: HierarchyEmployeeData[];
                    };
                };
            };
        };
    };
}

*/

interface CachedEmployeeData {
    basicInfo: Employee;
    // oracleDetails?: OracleDetailsResponse; // OLD Oracle cache field — uncomment to revert
    empDetails?: any;
}

const DEFAULT_IMAGE = "/profile.png";

interface EmployeeHierarchySearchProps {
    oracleId: string;
    onSuccess?: (oracleId: string, data?: any) => void;
    onError?: (oracleId: string, error?: string) => void;
    onLoading?: (isLoading: boolean) => void;
}

const EmployeeHierarchySearch: React.FC<EmployeeHierarchySearchProps> = ({
    oracleId,
    onSuccess,
    onError,
    onLoading
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const [searchedEmployee, setSearchedEmployee] = useState<Employee | null>(null);
    const [hierarchyData, setHierarchyData] = useState<Employee[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [exportProgress, setExportProgress] = useState<ExportProgress>({
        current: 0,
        total: 0,
        phase: 'idle'
    });
    const [showProgressIndicator, setShowProgressIndicator] = useState<boolean>(false);
    const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');
    const [searchFailed, setSearchFailed] = useState<boolean>(false);
    const [employeeDataCache, setEmployeeDataCache] = useState<Map<string, CachedEmployeeData>>(new Map());
    const [loadingProgress, setLoadingProgress] = useState<LoadingProgress>({
        show: false,
        message: "Starting hierarchy search...",
        current: 0,
        total: 0
    });

    const [toasts, setToasts] = useState<ToastNotification[]>([]);
    const [nextToastId, setNextToastId] = useState(1);

    const showToast = (message: string, type: 'success' | 'error' | 'warning') => {
        const newToast: ToastNotification = {
            id: nextToastId,
            message,
            type
        };
        setToasts(prev => [...prev, newToast]);
        setNextToastId(prev => prev + 1);

        setTimeout(() => {
            removeToast(newToast.id);
        }, 5000);
    };

    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    useEffect(() => {
        return () => {
            dispatch(resetEmpDetails());
            // dispatch(resetEmpOracleDetails()); // OLD Oracle cleanup — uncomment to revert
            dispatch(resetOrgChart()); // NEW PostgreSQL cleanup
        };
    }, [dispatch]);

    useEffect(() => {
    if (oracleId && oracleId.trim() !== "") {
        handleSearchWithProgress();
    }
    }, [oracleId]);

    const processEmployeeData = (
        empDetails: { data: any } | null,
        empOracleDetails: OracleDetailsResponse | null,
        basicEmployeeData: Partial<Employee>
    ): ProcessedEmployeeData => {
        const processedData: ProcessedEmployeeData = {
            name: basicEmployeeData?.name || "",
            jobLevel: "",
            jobTitle: "",
            timeAtTTEC: "",
            time_in_current_role: "",
            education_degree: "",
            certificates: "",
            location_desc: "",
            willingness_to_relocate: "",
            next_role_preference: "",
            timing_for_next_role: "",
            last_year_top_achievement: "",
            last_year_personal_development_review: "",
            goal_next_two_quarters: "",
            future_career_aspirations: "",
            redo_for_previous_business_year: "",
            image: basicEmployeeData?.imageUrl || "",
            emp_languages: [],
            EmpLocationPreferences: [],
            work_experiences: [],
            EmpResourceGroup: [],
            EmpTtecCommunities: [],
            EmpLeadershipCulture: [],
            oracle_id: basicEmployeeData?.oracleId || "",
            manager_oracle: "",
            manager_email: "",
            country: "",
            employee_status: "",
            accomplishments: "",
            redo: "",
            goals: "",
            resourceGroup: "",
            ttecCommunity: "",
            strength: "",
            development: "",
            ttecRoles: [],
            languages: [],
            locationPreferences: [],
            strengthsDisplayed: [],
            areasOfDevelopment: [],
            externalRoles: [],
        };

        if (empDetails?.data) {
            const empData = empDetails.data;
            processedData.time_in_current_role = empData.time_in_current_role || "";
            processedData.education_degree = empData.education_degree || "";
            processedData.certificates = empData.certificates || "";
            processedData.willingness_to_relocate = empData.willingness_to_relocate || "";
            processedData.next_role_preference = empData.next_role_preference || "";
            processedData.timing_for_next_role = empData.timing_for_next_role || "";
            processedData.last_year_top_achievement = empData.last_year_top_achievement || "";
            processedData.last_year_personal_development_review = empData.last_year_personal_development_review || "";
            processedData.goal_next_two_quarters = empData.goal_next_two_quarters || "";

            if (empData.image) {
                processedData.image = empData.image;
            }

            processedData.emp_languages = empData.emp_languages || [];
            processedData.EmpLocationPreferences = empData.EmpLocationPreferences || [];
            processedData.work_experiences = empData.work_experiences || [];
            processedData.EmpResourceGroup = empData.EmpResourceGroup || [];
            processedData.EmpTtecCommunities = empData.EmpTtecCommunities || [];
            processedData.EmpLeadershipCulture = empData.EmpLeadershipCulture || [];

            processedData.future_career_aspirations = empData.future_career_aspirations || "";
            processedData.redo_for_previous_business_year = empData.redo_for_previous_business_year || "";

            processedData.accomplishments = empData.last_year_top_achievement || "";
            processedData.redo = empData.last_year_personal_development_review || "";
            processedData.goals = empData.goal_next_two_quarters || "";

            if (Array.isArray(empData.EmpTtecCommunities) && empData.EmpTtecCommunities.length > 0) {
                processedData.ttecCommunity = empData.EmpTtecCommunities[0].ttec_communities || "";
            }

            if (Array.isArray(empData.work_experiences)) {
                processedData.ttecRoles = empData.work_experiences
                    .filter((w: any) => w.company_name === "TTEC")
                    .map((w: any) => ({
                        emp_work_experience_uid: w.emp_work_experience_uid,
                        previousRole: w.previous_position_role,
                        previousStartingDate: w.starting_date,
                        previousEndingDate: w.end_date,
                        currentRole: w.current_position || false,
                    }));

                processedData.externalRoles = empData.work_experiences
                    .filter((w: any) => w.company_name !== "TTEC")
                    .map((w: any) => ({
                        emp_work_experience_uid: w.emp_work_experience_uid,
                        roleTitle: w.previous_position_role,
                        company: w.company_name,
                        roleStartingDate: w.starting_date,
                        roleEndingDate: w.end_date,
                    }));
            }

            if (Array.isArray(empData.EmpLeadershipCulture)) {
                processedData.strengthsDisplayed = empData.EmpLeadershipCulture
                    .filter((lc: any) => lc.flag === "STRENGTH")
                    .map((lc: any) => lc.leadership_culture_dropdown_sd?.leadership_culture_dropdown_uid)
                    .filter(Boolean);

                processedData.areasOfDevelopment = empData.EmpLeadershipCulture
                    .filter((lc: any) => lc.flag === "DEVELOPMENT")
                    .map((lc: any) => lc.leadership_culture_dropdown_sd?.leadership_culture_dropdown_uid)
                    .filter(Boolean);
            }

            if (Array.isArray(empData.emp_languages)) {
                processedData.languages = empData.emp_languages
                    .map((l: any) => l.language_dropdown_uid)
                    .filter(Boolean);
            }

            if (Array.isArray(empData.EmpLocationPreferences)) {
                processedData.locationPreferences = empData.EmpLocationPreferences
                    .map((loc: any) => loc.location_dropdown_uid)
                    .filter(Boolean);
            }
        }

        if (empOracleDetails?.data?.response?.message) {
            const oracleMsg: OracleEmployeeMessage = empOracleDetails.data.response.message;

            processedData.oracle_id = oracleMsg.Employee_Oracle || processedData.oracle_id;
            processedData.name = `${oracleMsg.Employee_FirstName || ""} ${oracleMsg.Employee_LastName || ""}`.trim() || processedData.name;
            processedData.manager_oracle = oracleMsg.Manager_Oracle || "";
            processedData.manager_email = oracleMsg.Manager_Email || "";
            processedData.country = oracleMsg.Country || "";
            processedData.location_desc = oracleMsg.Location_Desc || "";
            processedData.employee_status = oracleMsg.Employee_Status || "";
            processedData.jobLevel = oracleMsg.Employee_Level || "";

            if (oracleMsg.Job_Code) {
                processedData.jobTitle = oracleMsg.Job_Code.includes(".")
                    ? oracleMsg.Job_Code.split(".")[1].trim()
                    : oracleMsg.Job_Code;
            }

            if (oracleMsg.Employee_Image) {
                processedData.image = oracleMsg.Employee_Image;
            }

            if (oracleMsg.Hire_Date) {
                const hireDate = new Date(oracleMsg.Hire_Date);
                if (!isNaN(hireDate.getTime())) {
                    const currentDate = new Date();
                    const yearDiff = currentDate.getFullYear() - hireDate.getFullYear();
                    const monthDiff = currentDate.getMonth() - hireDate.getMonth();

                    if (monthDiff < 0) {
                        processedData.timeAtTTEC = `${yearDiff - 1} Year${yearDiff - 1 !== 1 ? "s" : ""} ${12 + monthDiff} Month${12 + monthDiff !== 1 ? "s" : ""}`;
                    } else {
                        processedData.timeAtTTEC = `${yearDiff} Year${yearDiff !== 1 ? "s" : ""} ${monthDiff} Month${monthDiff !== 1 ? "s" : ""}`;
                    }
                }
            }
        }

        return processedData;
    };

    const createEmptyEmpDetailsData = (): EmpDetails => {
        return {
            emp_details_uid: 0,
            oracle_id: "",
            email_id: "",
            image: "",
            year: new Date().getFullYear().toString(),
            half_year: "H1",
            current_location: "Not Available",
            willingness_to_relocate: "Not Specified",
            next_role_preference: "Not Specified",
            timing_for_next_role: "Not Specified",
            last_year_top_achievement: "Not Specified",
            last_year_personal_development_review: "Not Specified",
            goal_next_two_quarters: "Not Specified",
            certificates: "Not Available",
            education_degree: "Not Available",
            future_career_aspirations: "Not Available",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            work_experiences: [],
            emp_languages: [],
            EmpLocationPreferences: [],
            EmpResourceGroup: [],
            EmpTtecCommunities: [],
            EmpLeadershipCulture: []
        };
    };
/*
    const createEmptyOracleDetailsData = (): OracleDetailsResponse => {
    return {
    data: {
    response: {
    message: {
    Employee_Oracle: "",
    Employee_FirstName: "",
    Employee_LastName: "",
    Manager_Oracle: "",
    Manager_Email: "",
    Country: "Not Available",
    Location_Desc: "Not Available",
    Employee_Status: "Active",
    Employee_Level: "Not Available",
    Job_Code: "Not Available",
    Employee_Image: undefined,
    Hire_Date: undefined
                    }
                }
            }
        };
};
*/

const handleSearchWithProgress = async (): Promise<void> => {
    if (!oracleId.trim()) {
        if (onError) {
            onError(oracleId, "Oracle ID is required");
        }
        return;
    }

    if (onLoading) {
        onLoading(true);
    }

    setEmployeeDataCache(new Map());
    setLoading(true);
    setError(null);
    setSearchFailed(false);
    setLoadingProgress({
        show: true,
        message: "Initializing search...",
        current: 0,
        total: 1
    });

    try {
        await fetchHierarchyWithProgress(oracleId.trim());
        
        if (onSuccess) {
            onSuccess(oracleId, hierarchyData);
        }
    } catch (err) {
        const errorMessage = "Failed to search for employee hierarchy";
        setError(errorMessage);
        setSearchFailed(true);
        
        if (onError) {
            onError(oracleId, errorMessage);
        }
    } finally {
        setLoading(false);
        setLoadingProgress({
            show: false,
            message: "",
            current: 0,
            total: 0
        });
        
        if (onLoading) {
            onLoading(false);
        }
    }
};

    const fetchHierarchyWithProgress = async (oracleId: string): Promise<void> => {
        try {
            setLoadingProgress(prev => ({
                ...prev,
                message: `Fetching hierarchy for Oracle ID: ${oracleId}`,
                current: 0,
                total: 1
            }));

            const hierarchyTree = await buildHierarchyTreeWithProgress(oracleId, 0, undefined, true);

            const employeeFound = hierarchyTree.name && hierarchyTree.name.trim() !== "";

            if (employeeFound) {
                setSearchFailed(false);

                const processedTree = { ...hierarchyTree, isExpanded: true };

                setSearchedEmployee(processedTree);
                setHierarchyData([processedTree]);

                if (processedTree.reportees.length > 0) {
                    showToast(`Successfully loaded hierarchy for ${processedTree.name} with ${processedTree.reportees.length} reportees`, "success");
                } else {
                    showToast(`Found employee ${processedTree.name} but no reportees detected`, "success");
                }
            } else {
                setSearchFailed(true);
                setSearchedEmployee(null);
                setHierarchyData([]);
                showToast(`No Hierarchy found with Oracle ID: ${oracleId}`, "error");
            }

        } catch (error) {
            setSearchFailed(true);
            setSearchedEmployee(null);
            setHierarchyData([]);
            setError("Failed to build hierarchy tree");
        }
    };

    /* ────────────────────────────────────────────────────────────────────────
     * OLD Oracle-based buildHierarchyTreeWithProgress
     * (commented — uncomment this and remove the new PG version to revert)
     * ────────────────────────────────────────────────────────────────────────
     *
     * const buildHierarchyTreeWithProgress = async (
     *     oracleId: string,
     *     level: number = 0,
     *     knownEmail?: string,
     *     isProgressRoot: boolean = true
     * ): Promise<Employee> => {
     *
     *     if (isProgressRoot && level === 0) {
     *         setLoadingProgress(prev => ({ ...prev, message: `Processing root employee: ${oracleId}`, current: 1, total: 1 }));
     *     }
     *
     *     let employee: Employee = {
     *         name: "", oracleId, email: knownEmail || "", jobLevel: "", jobTitle: "", timeAtTTEC: "",
     *         imageUrl: DEFAULT_IMAGE, managerId: "", managerName: "", reportees: [], isExpanded: level < 2, level
     *     };
     *
     *     let employeeEmail = knownEmail;
     *     let reporteesData: HierarchyEmployeeData[] = [];
     *
     *     try {
     *         const hierarchyResult = await dispatch(fetchEmpOracleHierarchyDetails(oracleId));
     *
     *         if (hierarchyResult.type === 'empOracleHierarchyDetails/fetchEmpOracleHierarchyDetails/fulfilled') {
     *             const hierarchyData = hierarchyResult.payload as HierarchyResponse;
     *
     *             if (hierarchyData?.response?.message && !Array.isArray(hierarchyData.response.message)) {
     *                 const singleEmployeeData = hierarchyData.response.message as any;
     *                 if (singleEmployeeData.Employee_FirstName || singleEmployeeData.Employee_Oracle) {
     *                     employee.name = `${singleEmployeeData.Employee_FirstName || ""} ${singleEmployeeData.Employee_LastName || ""}`.trim();
     *                     if (singleEmployeeData.Manager_Email) {
     *                         employeeEmail = singleEmployeeData.Manager_Email;
     *                         employee.email = employeeEmail ?? "";
     *                     }
     *                     employee.jobLevel = singleEmployeeData.Employee_Level || "";
     *                     if (singleEmployeeData.Job_Code) {
     *                         employee.jobTitle = singleEmployeeData.Job_Code.includes(".")
     *                             ? singleEmployeeData.Job_Code.split(".")[1].trim()
     *                             : singleEmployeeData.Job_Code;
     *                     }
     *                     if (singleEmployeeData.Employee_Image) {
     *                         employee.imageUrl = singleEmployeeData.Employee_Image;
     *                     }
     *                 }
     *             }
     *
     *             // Parse reportees from various Oracle response shapes
     *             if (hierarchyData?.data?.response?.message?.data && Array.isArray(hierarchyData.data.response.message.data)) {
     *                 reporteesData = hierarchyData.data.response.message.data;
     *             } else if (hierarchyData?.response?.message?.data && Array.isArray(hierarchyData.response.message.data)) {
     *                 reporteesData = hierarchyData.response.message.data;
     *             } else if (hierarchyData?.data?.response?.message?.result?.returnvals?.["0"]?.value &&
     *                 Array.isArray(hierarchyData.data.response.message.result.returnvals["0"].value)) {
     *                 reporteesData = hierarchyData.data.response.message.result.returnvals["0"].value;
     *             } else if (hierarchyData?.response?.message?.result?.returnvals?.["0"]?.value &&
     *                 Array.isArray(hierarchyData.response.message.result.returnvals["0"].value)) {
     *                 reporteesData = hierarchyData.response.message.result.returnvals["0"].value;
     *             }
     *
     *             if (isProgressRoot && level === 0 && reporteesData.length > 0) {
     *                 const estimatedTotal = calculateHierarchySize(reporteesData, level);
     *                 setLoadingProgress(prev => ({ ...prev, total: estimatedTotal, message: `Found ${reporteesData.length} direct reportees...` }));
     *             }
     *
     *             let oracleDetailsData: OracleDetailsResponse | null = null;
     *             if (employeeEmail && (!employee.name || employee.name.trim() === "")) {
     *                 setLoadingProgress(prev => ({ ...prev, message: `Fetching Oracle details for: ${employeeEmail}` }));
     *                 try {
     *                     const empOracleResult = await dispatch(fetchEmpOracleDetails(employeeEmail));
     *                     if (empOracleResult.type === 'empOracleDetails/fetchEmpOracleDetails/fulfilled') {
     *                         oracleDetailsData = empOracleResult.payload as OracleDetailsResponse;
     *                         if (oracleDetailsData?.data?.response?.message) {
     *                             const msg = oracleDetailsData.data.response.message;
     *                             employee.name = `${msg.Employee_FirstName || ""} ${msg.Employee_LastName || ""}`.trim();
     *                             employee.jobLevel = msg.Employee_Level || employee.jobLevel;
     *                             if (!employee.jobTitle && msg.Job_Code) {
     *                                 employee.jobTitle = msg.Job_Code.includes(".") ? msg.Job_Code.split(".")[1].trim() : msg.Job_Code;
     *                             }
     *                             if (msg.Employee_Image && employee.imageUrl === DEFAULT_IMAGE) {
     *                                 employee.imageUrl = msg.Employee_Image;
     *                             }
     *                         }
     *                     }
     *                 } catch (error) {}
     *             }
     *
     *             if (employee.email) {
     *                 setEmployeeDataCache(prev => {
     *                     const newCache = new Map(prev);
     *                     newCache.set(employee.email, { basicInfo: { ...employee }, oracleDetails: oracleDetailsData || undefined });
     *                     return newCache;
     *                 });
     *             }
     *
     *             if (reporteesData.length > 0 && level < 4) {
     *                 // ... recursive Oracle reportee processing with HierarchyEmployeeData parsing,
     *                 //     name splitting on comma, hire_date tenure calculation, fetchEmpOracleDetails etc.
     *                 //     (full old code available in git history)
     *             }
     *
     *             if ((!employee.name || employee.name.trim() === "") && reporteesData.length > 0) {
     *                 const firstReportee = reporteesData[0];
     *                 if (firstReportee.manager_name) employee.name = firstReportee.manager_name;
     *             }
     *
     *             if (isProgressRoot && level === 0) {
     *                 setLoadingProgress(prev => ({ ...prev, message: `Completed hierarchy for ${employee.name}`, current: prev.total }));
     *             }
     *         }
     *     } catch (error) {}
     *
     *     return employee;
     * };
     * ──────────────────────────────────────────────────────────────────────── */

    // ── NEW PostgreSQL-based buildHierarchyTreeWithProgress ──
    const buildHierarchyTreeWithProgress = async (
        oracleId: string,
        level: number = 0,
        knownEmail?: string,
        isProgressRoot: boolean = true
    ): Promise<Employee> => {

        if (isProgressRoot && level === 0) {
            setLoadingProgress(prev => ({
                ...prev,
                message: `Processing root employee: ${oracleId}`,
                current: 1,
                total: 1
            }));
        }

        let employee: Employee = {
            name: "",
            oracleId: oracleId,
            email: knownEmail || "",
            jobLevel: "",
            jobTitle: "",
            timeAtTTEC: "",
            imageUrl: DEFAULT_IMAGE,
            managerId: "",
            managerName: "",
            reportees: [],
            isExpanded: level < 2,
            level: level
        };

        try {
            const empResult = await dispatch(fetchOrgChartEmployee(oracleId));

            if (empResult.type === 'orgChart/fetchOrgChartEmployee/fulfilled') {
                const empData = empResult.payload as OrgChartEmployee;
                employee.name = empData.EmployeeFullName || "";
                employee.email = empData.WorkEmail || knownEmail || "";
                employee.jobTitle = empData.JobTitle || "";
            }

            const directsResult = await dispatch(fetchOrgChartDirects(oracleId));
            let directReportees: OrgChartDirect[] = [];

            if (directsResult.type === 'orgChart/fetchOrgChartDirects/fulfilled') {
                directReportees = directsResult.payload as OrgChartDirect[];
            }

            if (isProgressRoot && level === 0 && directReportees.length > 0) {
                const estimatedTotal = calculateHierarchySize(
                    directReportees.map(d => ({
                        employee_id: d.EmployeeNumber,
                        employee_name: d.EmployeeFullName,
                        work_email: d.WorkEmail,
                    })),
                    level
                );
                setLoadingProgress(prev => ({
                    ...prev,
                    total: estimatedTotal,
                    message: `Found ${directReportees.length} direct reportees, processing 3-level hierarchy...`
                }));
            }

            if (employee.email) {
                setEmployeeDataCache(prev => {
                    const newCache = new Map(prev);
                    newCache.set(employee.email, {
                        basicInfo: { ...employee },
                    });
                    return newCache;
                });
            }

            if (directReportees.length > 0 && level < 4) {
                if (isProgressRoot) {
                    setLoadingProgress(prev => ({
                        ...prev,
                        message: `Processing ${directReportees.length} reportees at level ${level + 1}...`
                    }));
                }

                const reporteePromises = directReportees.map(async (direct: OrgChartDirect, index: number) => {
                    const reporteeOracleId = direct.EmployeeNumber?.trim();
                    const reporteeEmail = direct.WorkEmail?.trim();

                    if (!reporteeOracleId) {
                        return null;
                    }

                    try {
                        if (isProgressRoot) {
                            setLoadingProgress(prev => ({
                                ...prev,
                                message: `Level ${level + 1} - Processing ${index + 1}/${directReportees.length}: ${direct.EmployeeFullName || reporteeOracleId}`,
                                current: Math.min(prev.current + 1, prev.total)
                            }));
                        }

                        const reporteeEmployee: Employee = {
                            name: direct.EmployeeFullName || "",
                            oracleId: reporteeOracleId,
                            email: reporteeEmail || "",
                            jobLevel: "",
                            jobTitle: direct.JobTitle || "",
                            timeAtTTEC: "",
                            imageUrl: DEFAULT_IMAGE,
                            managerId: oracleId,
                            managerName: employee.name,
                            reportees: [],
                            isExpanded: level + 1 < 2,
                            level: level + 1
                        };

                        setEmployeeDataCache(prev => {
                            const newCache = new Map(prev);
                            newCache.set(reporteeEmployee.email || reporteeOracleId, {
                                basicInfo: { ...reporteeEmployee }
                            });
                            return newCache;
                        });

                        if (level < 3) {
                            try {
                                const subHierarchy = await buildHierarchyTreeWithProgress(
                                    reporteeOracleId,
                                    level + 1,
                                    reporteeEmail,
                                    false
                                );
                                reporteeEmployee.reportees = subHierarchy.reportees;

                                if (subHierarchy.name && !reporteeEmployee.name) {
                                    reporteeEmployee.name = subHierarchy.name;
                                }
                                if (subHierarchy.jobTitle && !reporteeEmployee.jobTitle) {
                                    reporteeEmployee.jobTitle = subHierarchy.jobTitle;
                                }

                                setEmployeeDataCache(prev => {
                                    const newCache = new Map(prev);
                                    const existing = newCache.get(reporteeEmployee.email || reporteeOracleId) || { basicInfo: reporteeEmployee };
                                    newCache.set(reporteeEmployee.email || reporteeOracleId, {
                                        ...existing,
                                        basicInfo: { ...reporteeEmployee }
                                    });
                                    return newCache;
                                });
                            } catch (error) {
                            }
                        }

                        return reporteeEmployee;

                    } catch (error) {
                        return null;
                    }
                });

                const reporteeResults = await Promise.all(reporteePromises);
                employee.reportees = reporteeResults.filter((r): r is Employee => r !== null);
            }

            if (isProgressRoot && level === 0) {
                setLoadingProgress(prev => ({
                    ...prev,
                    message: `Completed 3-level hierarchy for ${employee.name}`,
                    current: prev.total,
                }));
            }

        } catch (error) {
        }

        return employee;
    };

    // OLD: const calculateHierarchySize = (reporteesData: HierarchyEmployeeData[], level: number): number => {
    const calculateHierarchySize = (reporteesData: { employee_id: string }[], level: number): number => {
        if (level >= 4) return reporteesData.length; 

        const currentLevelSize = reporteesData.length;
        let totalEstimate = currentLevelSize;

        if (level === 0) {
            const level2Estimate = currentLevelSize * 0.6; 
            const level3Estimate = level2Estimate * 0.4;  
            totalEstimate = Math.ceil(currentLevelSize + level2Estimate + level3Estimate);
        } else if (level === 1) {
            const level3Estimate = currentLevelSize * 0.4;
            totalEstimate = Math.ceil(currentLevelSize + level3Estimate);
        }

        return Math.min(totalEstimate, 100); 
    };

    const toggleExpand = (targetEmployee: Employee) => {
        const updateTree = (employees: Employee[]): Employee[] => {
            return employees.map(emp => {
                if (emp.oracleId === targetEmployee.oracleId && emp.email === targetEmployee.email) {
                    return { ...emp, isExpanded: !emp.isExpanded };
                }
                if (emp.reportees.length > 0) {
                    return { ...emp, reportees: updateTree(emp.reportees) };
                }
                return emp;
            });
        };

        setHierarchyData(updateTree(hierarchyData));
    };

    const collectAllEmployees = (employees: Employee[]): Employee[] => {
        let allEmployees: Employee[] = [];

        employees.forEach(emp => {
            if (emp.level !== 0) {
                allEmployees.push(emp);
            }

            if (emp.reportees.length > 0) {
                allEmployees = allEmployees.concat(collectAllEmployees(emp.reportees));
            }
        });

        return allEmployees;
    };


    const handleCheckboxChange = (employeeEmail: string) => {
        const newSelected = new Set(selectedEmployees);
        if (newSelected.has(employeeEmail)) {
            newSelected.delete(employeeEmail);
        } else {
            newSelected.add(employeeEmail);
        }
        setSelectedEmployees(newSelected);

        const allEmployees = collectAllEmployees(hierarchyData);
        setSelectAll(newSelected.size === allEmployees.length);
    };

    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedEmployees(new Set());
            setSelectAll(false);
        } else {
            const allEmployees = collectAllEmployees(hierarchyData);
            const allEmails = new Set(allEmployees.map(emp => emp.email));
            setSelectedEmployees(allEmails);
            setSelectAll(true);
        }
    };

    const HierarchyProgressIndicator = () => {
        if (!loadingProgress.show) return null;
        const pct = loadingProgress.total > 0
            ? Math.round((loadingProgress.current / loadingProgress.total) * 100) : 0;

        return (
            <>
                <div className="loading-backdrop" />
                <div className="loading-modal">
                    <div className="loading-modal__title">Building Organization Tree</div>
                    <div className="loading-modal__message">{loadingProgress.message}</div>
                    <div className="loading-modal__stats">
                        {loadingProgress.current} of {loadingProgress.total} nodes processed ({pct}%)
                    </div>
                    <div className="progress-bar-track">
                        <div className="progress-bar-fill progress-bar-fill--blue" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="loading-modal__hint">This may take a moment for large hierarchies...</div>
                </div>
            </>
        );
    };
    const NODE_WIDTH = 180;
    const HORIZONTAL_GAP = 24;
    const CONNECTOR_COLOR = "#90a4ae";

    function getNodeColor(level: number): string {
        switch (level) {
            case 0: return "#1976d2";
            case 1: return "#2196f3";
            case 2: return "#42a5f5";
            case 3: return "#64b5f6";
            default: return "#90caf9";
        }
    }

    function getSubtreeWidth(employee: Employee): number {
        const isRootEmployee = employee.level === 0;
        const showChildren = employee.reportees.length > 0 && (isRootEmployee || employee.isExpanded);

        if (!showChildren || employee.reportees.length === 0) {
            return NODE_WIDTH;
        }

        const childrenTotalWidth = employee.reportees.reduce((sum, child, i) => {
            return sum + getSubtreeWidth(child) + (i > 0 ? HORIZONTAL_GAP : 0);
        }, 0);

        return Math.max(NODE_WIDTH, childrenTotalWidth);
    }

    const HorizontalConnector: React.FC<{ reportees: Employee[] }> = ({ reportees }) => {
        const childWidths = reportees.map(r => getSubtreeWidth(r));
        const totalWidth = childWidths.reduce((a, b) => a + b, 0) + (reportees.length - 1) * HORIZONTAL_GAP;

        const firstCenter = childWidths[0] / 2;
        const lastCenter = totalWidth - childWidths[childWidths.length - 1] / 2;

        const lineLeft = (firstCenter / totalWidth) * 100;
        const lineRight = ((totalWidth - lastCenter) / totalWidth) * 100;

        return (
            <div style={{
                position: "absolute", top: 0,
                left: `${lineLeft}%`, right: `${lineRight}%`,
                height: 2, backgroundColor: CONNECTOR_COLOR, zIndex: 1,
            }} />
        );
    };

    const renderTreeNode = (employee: Employee, isRoot: boolean = false) => {
        const hasReportees = employee.reportees.length > 0;
        const isRootEmployee = employee.level === 0;
        const showChildren = hasReportees && (isRootEmployee || employee.isExpanded);
        const subtreeWidth = getSubtreeWidth(employee);
        const isSelected = selectedEmployees.has(employee.email);

        return (
            <div
                key={`${employee.oracleId}-${employee.email}`}
                className="org-tree-node"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: `${subtreeWidth}px` }}
            >
                <div
                    className={`org-card ${isRootEmployee ? 'org-card--root' : ''}`}
                    style={{
                        backgroundColor: getNodeColor(employee.level),
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        width: `${NODE_WIDTH}px`,
                        textAlign: 'center',
                        position: 'relative',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        cursor: isRootEmployee ? 'default' : 'pointer',
                        border: `2px solid ${!isRootEmployee && isSelected ? '#ff9800' : 'transparent'}`,
                        zIndex: 2
                    }}
                    onClick={() => { if (!isRootEmployee) handleCheckboxChange(employee.email); }}
                >
                    <div style={{
                        position: 'absolute', top: 5, right: 5,
                        ...(isRootEmployee
                            ? { backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 4, padding: '2px 6px', fontSize: 8, fontWeight: 'bold' }
                            : { width: 16, height: 16, borderRadius: '50%', backgroundColor: isSelected ? '#ff9800' : 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 'bold' }
                        )
                    }}>
                        {isRootEmployee ? 'ROOT' : (isSelected ? '✓' : '')}
                    </div>

                    <img
                        src={employee.imageUrl}
                        alt={employee.name}
                        style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid white', marginBottom: 8, objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/profile.png'; }}
                    />
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, wordBreak: 'break-word', lineHeight: 1.2 }}>
                        {employee.name || 'Name Not Available'}
                    </div>
                    {!isRootEmployee && (
                        <div style={{ fontSize: 11, opacity: 0.9, marginBottom: 2 }}>
                            {employee.jobTitle || employee.jobLevel || 'Position N/A'}
                        </div>
                    )}
                    <div style={{ fontSize: 10, opacity: 0.8 }}>ID: {employee.oracleId}</div>

                    {hasReportees && (
                        <div style={{
                            position: 'absolute', bottom: -10, left: '50%', transform: 'translateX(-50%)',
                            backgroundColor: '#ff9800', color: 'white', borderRadius: 10,
                            padding: '2px 8px', fontSize: 10, fontWeight: 600, minWidth: 20, zIndex: 3
                        }}>
                            {employee.reportees.length}
                        </div>
                    )}
                </div>

                {hasReportees && !isRootEmployee && (
                    <button
                        onClick={(e) => { e.stopPropagation(); toggleExpand(employee); }}
                        style={{
                            backgroundColor: CONNECTOR_COLOR, color: 'white', border: 'none',
                            borderRadius: '50%', width: 22, height: 22, fontSize: 14,
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', zIndex: 10, marginTop: 8, lineHeight: 1
                        }}
                    >
                        {employee.isExpanded ? '−' : '+'}
                    </button>
                )}

                {showChildren && (
                    <>
                        <div style={{ width: 2, height: 30, backgroundColor: CONNECTOR_COLOR, flexShrink: 0 }} />
                        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                            {employee.reportees.length > 1 && <HorizontalConnector reportees={employee.reportees} />}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: `${HORIZONTAL_GAP}px` }}>
                                {employee.reportees.map((child) => (
                                    <div
                                        key={`${child.oracleId}-${child.email}`}
                                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: `${getSubtreeWidth(child)}px`, flexShrink: 0 }}
                                    >
                                        <div style={{ width: 2, height: 25, backgroundColor: CONNECTOR_COLOR, flexShrink: 0 }} />
                                        {renderTreeNode(child, false)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderHierarchyNode = (employee: Employee) => {
        const hasReportees = employee.reportees.length > 0;
        const isRootEmployee = employee.level === 0;
        const showChildren = hasReportees && (isRootEmployee || employee.isExpanded);

        return (
            <div key={`${employee.oracleId}-${employee.email}`} className="list-node">
                <div className={`list-item ${isRootEmployee ? 'list-item--root' : ''}`}>
                    {isRootEmployee ? (
                        <span className="badge bg-primary" style={{ fontSize: 9 }}>ROOT</span>
                    ) : (
                        <div className="list-item__checkbox">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedEmployees.has(employee.email)}
                                onChange={() => handleCheckboxChange(employee.email)}
                            />
                        </div>
                    )}

                    <img
                        src={employee.imageUrl}
                        alt={employee.name}
                        className="list-item__avatar"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/profile.png'; }}
                    />

                    <div className="list-item__info">
                        <span className="list-item__name">
                            {employee.name || 'Name Not Available'}
                            {isRootEmployee && (
                                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>Not Exportable</span>
                            )}
                        </span>
                        <span className="list-item__detail"><b>Email:</b> {employee.email}</span>
                        <span className="list-item__detail"><b>ID:</b> {employee.oracleId}</span>
                        <span className="list-item__detail"><b>Level:</b> {employee.jobLevel || 'N/A'}</span>
                        <span className="list-item__detail"><b>Title:</b> {employee.jobTitle || 'N/A'}</span>
                        <span className="list-item__detail"><b>Tenure:</b> {employee.timeAtTTEC || 'N/A'}</span>
                    </div>

                    <div className="list-item__actions">
                        {hasReportees && (
                            <span className="badge bg-primary">
                                {employee.reportees.length} Reportee{employee.reportees.length !== 1 ? 's' : ''}
                            </span>
                        )}
                        {hasReportees && !isRootEmployee && (
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => toggleExpand(employee)}
                                style={{ minWidth: 36 }}
                            >
                                <i className={`bi bi-chevron-${employee.isExpanded ? 'down' : 'right'}`} />
                            </button>
                        )}
                    </div>
                </div>

                {showChildren && (
                    <div className="list-node__children">
                        {employee.reportees.map(reportee => renderHierarchyNode(reportee))}
                    </div>
                )}
            </div>
        );
    };

    const generateBulkPDFBackground = async (
        employeeDataArray: ProcessedEmployeeData[],
        isExport: boolean = true
    ): Promise<{ blob: Blob; fileName: string } | null> => {
        if (!employeeDataArray || employeeDataArray.length === 0) {
            return null;
        }

        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'in',
            format: [14.65, 12.32],
            compress: true
        });

        setExportProgress(prev => ({ ...prev, phase: 'Generating PDF pages...' }));

        let successfulPages = 0;
        let isFirstPage = true;

        try {
            for (let i = 0; i < employeeDataArray.length; i++) {
                const employeeData = employeeDataArray[i];

                setExportProgress(prev => ({
                    current: i + 1,
                    total: employeeDataArray.length,
                    phase: `Generating page ${i + 1} of ${employeeDataArray.length}...`
                }));

                let offscreenDiv: HTMLDivElement | null = null;
                let root: ReturnType<typeof createRoot> | null = null;

                try {
                    if (!isFirstPage) {
                        pdf.addPage();
                    }

                    offscreenDiv = document.createElement("div");
                    offscreenDiv.style.position = "absolute";
                    offscreenDiv.style.left = "-9999px";
                    offscreenDiv.style.top = "-9999px";
                    offscreenDiv.style.width = '1700px';
                    offscreenDiv.style.fontSize = '13px';
                    offscreenDiv.style.lineHeight = '1.2';
                    offscreenDiv.style.color = "#000";
                    offscreenDiv.style.backgroundColor = "#fff";
                    offscreenDiv.style.padding = '50px 100px 50px 100px';
                    offscreenDiv.style.visibility = "visible";

                    document.body.appendChild(offscreenDiv);
                    root = createRoot(offscreenDiv);

                    await new Promise<void>((resolve, reject) => {
                        const timeout = setTimeout(() => {
                            reject(new Error(`Timeout rendering employee: ${employeeData.name}`));
                        }, 15000);

                        root!.render(React.createElement(CareerCardHtml, { data: employeeData }));

                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                clearTimeout(timeout);
                                resolve();
                            }, 500);
                        });
                    });

                    const canvas = await html2canvas(offscreenDiv, {
                        scale: 1.2,
                        useCORS: true,
                        backgroundColor: "#fff",
                        logging: false,
                        allowTaint: true,
                        removeContainer: false,
                        height: offscreenDiv.scrollHeight,
                        width: offscreenDiv.scrollWidth,
                        imageTimeout: 8000,
                        onclone: (clonedDoc: Document) => {
                            const clonedElement = clonedDoc.body.querySelector("div");
                            if (clonedElement) {
                                (clonedElement as HTMLElement).style.visibility = "visible";
                                (clonedElement as HTMLElement).style.display = "block";
                            }
                            const images = clonedDoc.querySelectorAll("img");
                            images.forEach((img) => {
                                img.crossOrigin = "anonymous";
                                img.onerror = () => {
                                    img.style.display = "none";
                                };
                            });
                        },
                    });

                    if (canvas.width === 0 || canvas.height === 0) {
                        if (!isFirstPage) {
                            const pageCount = pdf.getNumberOfPages();
                            pdf.deletePage(pageCount);
                        }
                        continue;
                    }

                    const imgData = canvas.toDataURL("image/jpeg", 0.85);
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = pdf.internal.pageSize.getHeight();
                    const imgProps = pdf.getImageProperties(imgData);
                    const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

                    if (imgHeight > pdfHeight) {
                        const scaledHeight = pdfHeight;
                        const scaledWidth = (imgProps.width * scaledHeight) / imgProps.height;
                        pdf.addImage(
                            imgData,
                            "JPEG",
                            (pdfWidth - scaledWidth) / 2,
                            0,
                            scaledWidth,
                            scaledHeight
                        );
                    } else {
                        pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeight);
                    }

                    successfulPages++;
                    isFirstPage = false;

                    canvas.remove();

                } catch (pageError) {
                    console.error(`Failed to generate PDF page for employee: ${employeeData.name}`, pageError);
                    if (!isFirstPage) {
                        try {
                            const pageCount = pdf.getNumberOfPages();
                            if (pageCount > successfulPages) {
                                pdf.deletePage(pageCount);
                            }
                        } catch (deleteError) {
                        }
                    }
                } finally {
                    try {
                        if (root) {
                            root.unmount();
                        }
                    } catch (unmountError) {
                    }
                    try {
                        if (offscreenDiv && offscreenDiv.parentNode) {
                            document.body.removeChild(offscreenDiv);
                        }
                    } catch (removeError) {
                    }
                }

                if ((window as any).gc) {
                    (window as any).gc();
                }

                if (i < employeeDataArray.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            if (successfulPages === 0) {
                throw new Error('No pages were successfully generated for the PDF');
            }

            const fileName = `Hierarchy_Career_Cards_${new Date().toISOString().split("T")[0].replace(/-/g, "")}.pdf`;
            const blob = pdf.output("blob");

            if (isExport) {
                pdf.save(fileName);
            }

            return { blob, fileName };

        } catch (error) {
            throw error;
        }
    };
    const handleExportToPDF = async (): Promise<void> => {
        if (selectedEmployees.size === 0) {
            showToast("Please select at least one employee to export", "warning");
            return;
        }

        setIsExporting(true);
        setShowProgressIndicator(true);

        const allEmployees = collectAllEmployees(hierarchyData);
        const selectedData = allEmployees.filter(emp => selectedEmployees.has(emp.email));

        setExportProgress({ current: 0, total: selectedData.length, phase: 'Starting export...' });

        try {
            showToast(`Starting export for ${selectedData.length} employees...`, 'success');

            const allEmployeeData: ProcessedEmployeeData[] = [];
            let cacheHitCount = 0;

            setExportProgress(prev => ({ ...prev, phase: 'Processing employee data...' }));

            const BATCH_SIZE = 3;
            for (let batchStart = 0; batchStart < selectedData.length; batchStart += BATCH_SIZE) {
                const batch = selectedData.slice(batchStart, batchStart + BATCH_SIZE);

                const batchPromises = batch.map(async (employee) => {
                    let empDetailsData: any = null;

                    /* OLD Oracle PDF export fetch (commented — uncomment to revert)
                     * let empOracleData: OracleDetailsResponse | null = null;
                     * const cachedData = employeeDataCache.get(employee.email);
                     * if (cachedData?.oracleDetails) {
                     *     empOracleData = cachedData.oracleDetails;
                     *     cacheHitCount++;
                     * } else {
                     *     try {
                     *         const empOracleResult = await dispatch(fetchEmpOracleDetails(employee.email));
                     *         if (empOracleResult.type === 'empOracleDetails/fetchEmpOracleDetails/fulfilled') {
                     *             empOracleData = empOracleResult.payload as OracleDetailsResponse;
                     *             setEmployeeDataCache(prev => {
                     *                 const newCache = new Map(prev);
                     *                 const existing = newCache.get(employee.email) || { basicInfo: employee };
                     *                 newCache.set(employee.email, { ...existing, oracleDetails: empOracleData || undefined });
                     *                 return newCache;
                     *             });
                     *         } else {
                     *             empOracleData = createEmptyOracleDetailsData();
                     *         }
                     *     } catch (error) {
                     *         empOracleData = createEmptyOracleDetailsData();
                     *     }
                     * }
                     * ─────────────────────────────────────────────────────────────── */

                    let orgChartData: OrgChartEmployee | null = null;
                    try {
                        const orgChartResult = await dispatch(fetchOrgChartEmployee(employee.oracleId));
                        if (orgChartResult.type === 'orgChart/fetchOrgChartEmployee/fulfilled') {
                            orgChartData = orgChartResult.payload as OrgChartEmployee;
                        }
                    } catch (error) {
                    }
                    
                    const empOracleData: OracleDetailsResponse = {
                        data: {
                            response: {
                                message: {
                                    Employee_Oracle: orgChartData?.EmployeeNumber || employee.oracleId,
                                    Employee_FirstName: orgChartData?.EmployeeFullName?.split(' ')[0] || "",
                                    Employee_LastName: orgChartData?.EmployeeFullName?.split(' ').slice(1).join(' ') || "",
                                    Job_Code: orgChartData?.JobTitle || "",
                                    Manager_Email: orgChartData?.WorkEmail || "",
                                }
                            }
                        }
                    };

                    try {
                        const empDetailsResult = await dispatch(fetchEmpDetails(employee.oracleId));
                        if (empDetailsResult.type === 'empDetails/fetchEmpDetails/fulfilled') {
                            empDetailsData = empDetailsResult.payload;
                        } else {
                            empDetailsData = createEmptyEmpDetailsData();
                        }
                    } catch (error) {
                        empDetailsData = createEmptyEmpDetailsData();
                    }

                    const processedData = processEmployeeData(
                        { data: empDetailsData },
                        empOracleData,
                        {
                            name: employee.name,
                            oracleId: employee.oracleId,
                            imageUrl: employee.imageUrl,
                            email: employee.email
                        }
                    );

                    return processedData;
                });

                const batchResults = await Promise.all(batchPromises);
                allEmployeeData.push(...batchResults);

                setExportProgress(prev => ({
                    ...prev,
                    current: Math.min(batchStart + BATCH_SIZE, selectedData.length),
                    phase: `Fetched data for ${Math.min(batchStart + BATCH_SIZE, selectedData.length)} of ${selectedData.length} employees...`
                }));
            }


            if (cacheHitCount > 0) {
                showToast(`Optimized export: ${cacheHitCount} employees used cached data`, 'success');
            }

            setExportProgress(prev => ({
                ...prev,
                current: selectedData.length,
                phase: 'All employee data processed, generating PDF...'
            }));

            const pdfResult = await generateBulkPDFBackground(allEmployeeData, true);

            if (pdfResult) {
                let message = `Successfully exported ${allEmployeeData.length} employee records to PDF!`;
                showToast(message, 'success');
            } else {
                showToast('PDF generation failed - no employee data could be processed. Please try again.', 'error');
            }

        } catch (error) {
            showToast('An error occurred while exporting to PDF. Please try again.', 'error');
        } finally {
            setIsExporting(false);
            setShowProgressIndicator(false);
            setExportProgress({ current: 0, total: 0, phase: 'idle' });
        }
    };

    const ProgressIndicator = () => {
        if (!isExporting || !showProgressIndicator) return null;
        const pct = exportProgress.total > 0
            ? Math.round((exportProgress.current / exportProgress.total) * 100) : 0;

        return (
            <>
                <div className="loading-backdrop" />
                <div className="loading-modal">
                    <button
                        className="loading-modal__close"
                        onClick={() => { setShowProgressIndicator(false); showToast('PDF generation continues in background.', 'success'); }}
                        title="Close (process continues in background)"
                        aria-label="Close progress indicator"
                    >
                        <i className="bi bi-x" aria-hidden="true" />
                    </button>
                    <div className="loading-modal__title" style={{ color: '#333' }}>Processing Hierarchy Career Cards</div>
                    <div className="loading-modal__message">{exportProgress.phase}</div>
                    <div className="loading-modal__stats">
                        {exportProgress.current} of {exportProgress.total} ({pct}%)
                    </div>
                    <div className="progress-bar-track progress-bar-track--lg">
                        <div className="progress-bar-fill progress-bar-fill--green" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="loading-modal__hint">You can close this dialog. The process will continue in the background.</div>
                </div>
            </>
        );
    };

    const allEmployeesList = hierarchyData.length > 0 ? collectAllEmployees(hierarchyData) : [];

    return (
        <div className="hierarchy-search">
            <HierarchyProgressIndicator />
            <ProgressIndicator />
            {toasts.map(toast => (
                <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
            ))}

            {hierarchyData.length > 0 && (
                <div className="hierarchy-toolbar">
                    <div className="hierarchy-toolbar__selection">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="selectAllHierarchy"
                            checked={selectAll}
                            onChange={handleSelectAllChange}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="selectAllHierarchy">
                            Select All ({allEmployeesList.length})
                        </label>
                    </div>

                    <span className="hierarchy-toolbar__count">
                        {selectedEmployees.size} of {allEmployeesList.length} selected
                    </span>

                    <div className="btn-group" role="group">
                        <input type="radio" className="btn-check" name="viewMode" id="treeView" checked={viewMode === 'tree'} onChange={() => setViewMode('tree')} />
                        <label className="btn btn-outline-primary btn-sm" htmlFor="treeView">
                            <i className="bi bi-diagram-3 me-1" />Tree View
                        </label>
                        <input type="radio" className="btn-check" name="viewMode" id="listView" checked={viewMode === 'list'} onChange={() => setViewMode('list')} />
                        <label className="btn btn-outline-primary btn-sm" htmlFor="listView">
                            <i className="bi bi-list-ul me-1" />List View
                        </label>
                    </div>

                    <div className="hierarchy-toolbar__spacer" />

                    {isExporting ? (
                        <button className="btn btn-success btn-sm" disabled>
                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                            Processing... ({exportProgress.current}/{exportProgress.total})
                        </button>
                    ) : (
                        <button className="btn btn-success btn-sm" onClick={handleExportToPDF} disabled={selectedEmployees.size === 0}>
                            <i className="bi bi-file-earmark-pdf me-1" />Export to PDF
                        </button>
                    )}
                </div>
            )}

            {error && (
                <div className="alert alert-danger hierarchy-alert d-flex align-items-center" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2" />
                    <div><strong>Error — </strong>{error}</div>
                </div>
            )}
            {!loading && !error && searchFailed && oracleId && (
                <div className="alert alert-danger hierarchy-alert d-flex align-items-center" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2" />
                    <div>No hierarchy found for Oracle ID: <strong>{oracleId}</strong>. Please verify and try again.</div>
                </div>
            )}

            {hierarchyData.length > 0 && (
                <>
                    <div className="hierarchy-header">
                        <i className={`bi ${viewMode === 'tree' ? 'bi-diagram-3' : 'bi-list-ul'}`} />
                        Organizational {viewMode === 'tree' ? 'Tree' : 'List'}
                        {searchedEmployee && (
                            <span className="hierarchy-header__badge">Root: {searchedEmployee.name}</span>
                        )}
                    </div>

                    {viewMode === 'tree' ? (
                        <div className="org-tree-container">
                            <div className="org-tree-wrapper">
                                {hierarchyData.map(employee => renderTreeNode(employee, true))}
                            </div>
                        </div>
                    ) : (
                        <div className="hierarchy-list">
                            {hierarchyData.map(employee => renderHierarchyNode(employee))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EmployeeHierarchySearch;